import { useEffect, useMemo, useRef } from 'react';

import { useI18n } from '../i18n/I18nProvider';

export default function ApiKeyDialog({
  open,
  onClose,
  apiKeyInput,
  setApiKeyInput,
  statusVariant, // connected | warning
  statusText,
  onSave,
  onClear,
  canClear,
  useProxy,
  devProxyHint,
}) {
  const { t } = useI18n();
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(e) {
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus?.(), 50);
  }, [open]);

  const badgeClassName = useMemo(() => {
    if (statusVariant === 'connected') return 'Badge Badge--ok';
    return 'Badge Badge--warn';
  }, [statusVariant]);

  if (!open) return null;

  return (
    <div className="DialogRoot" role="dialog" aria-modal="true" aria-label={t('settings.apiKeyDialogTitle')}>
      <button className="DialogBackdrop" type="button" onClick={onClose} aria-label={t('common.close')} />
      <div className="Dialog">
        <div className="DialogHeader">
          <div>
            <div className="DialogTitle">{t('settings.apiKeyDialogTitle')}</div>
            <div className={badgeClassName}>{statusText}</div>
          </div>
          <button className="IconBtn" type="button" onClick={onClose} aria-label={t('common.close')}>
            ✕
          </button>
        </div>

        <div className="DialogBody">
          {useProxy ? (
            <div className="InlineHint" style={{ marginTop: 0 }}>
              {t('settings.apiKeyDialogProxyNote')}
            </div>
          ) : null}

          {devProxyHint ? (
            <div className="InlineWarn" style={{ marginTop: 12 }}>
              {devProxyHint}
            </div>
          ) : null}

          <div className="Grid Grid--single" style={{ marginTop: 12 }}>
            <label className="Field" htmlFor="api-key-dialog">
              <div className="Field-label">{t('settings.apiKey')}</div>
              <input
                id="api-key-dialog"
                type="password"
                autoComplete="off"
                placeholder={t('settings.apiKeyPlaceholder')}
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                ref={inputRef}
              />
            </label>

            <div className="Row Row--tight">
              <button className="Btn" type="button" onClick={onSave}>
                {t('common.save')}
              </button>
              <button className="Btn Btn--ghost" type="button" onClick={onClear} disabled={!canClear}>
                {t('settings.clearLocalKey')}
              </button>
            </div>

            <div className="InlineHint">{t('settings.clientOnlyHint')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
